import React, { useEffect, useState } from 'react';
import { Award, Loader2, Trophy, TrendingUp, Users } from 'lucide-react';
import { adminApi } from '../lib/api';

const formatFCFA = (n) => new Intl.NumberFormat('fr-FR').format(n);

const TIER_META = {
  Bronze: { color: 'bg-orange-100 text-orange-800 border-orange-200', icon: '🥉' },
  Argent: { color: 'bg-slate-100 text-slate-800 border-slate-200', icon: '🥈' },
  Or: { color: 'bg-amber-100 text-amber-800 border-amber-200', icon: '🥇' },
  Platine: { color: 'bg-purple-100 text-purple-800 border-purple-200', icon: '💎' }
};

const LoyaltyPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.getLoyaltyCustomers()
      .then(setItems)
      .finally(() => setLoading(false));
  }, []);

  const stats = {
    total: items.length,
    active: items.filter(c => c.points > 0).length,
    revenue: items.reduce((s, c) => s + (c.total_spent || 0), 0),
    tiers: {
      Bronze: items.filter(c => c.tier === 'Bronze').length,
      Argent: items.filter(c => c.tier === 'Argent').length,
      Or: items.filter(c => c.tier === 'Or').length,
      Platine: items.filter(c => c.tier === 'Platine').length,
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-bold text-slate-900">Programme de fidélité</h3>
        <p className="text-sm text-slate-500">1 point par 1 000 F CFA dépensés · Récompenses par palier</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-2xl p-4 border border-slate-200">
          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-widest"><Users className="w-3 h-3" /> Membres</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{stats.total}</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-slate-200">
          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-widest"><TrendingUp className="w-3 h-3" /> Actifs</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{stats.active}</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-slate-200">
          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-widest"><Award className="w-3 h-3" /> CA total</div>
          <div className="text-xl font-bold text-[#E89534] mt-1">{formatFCFA(stats.revenue)} F</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-slate-200">
          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-widest"><Trophy className="w-3 h-3" /> Or + Platine</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{stats.tiers.Or + stats.tiers.Platine}</div>
        </div>
      </div>

      {/* Tier legend */}
      <div className="bg-white rounded-2xl p-5 border border-slate-200">
        <div className="text-sm font-semibold text-slate-700 mb-3">Paliers & récompenses</div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          {[
            { name: 'Bronze', pts: '0 - 29 pts', perk: 'Newsletter exclusive' },
            { name: 'Argent', pts: '30 - 99 pts', perk: '-5% sur les balades' },
            { name: 'Or', pts: '100 - 299 pts', perk: '-10% + collation offerte' },
            { name: 'Platine', pts: '300+ pts', perk: '-15% + balade privée annuelle' },
          ].map((t) => (
            <div key={t.name} className={`p-3 rounded-xl border ${TIER_META[t.name].color}`}>
              <div className="text-lg">{TIER_META[t.name].icon}</div>
              <div className="font-bold text-sm">{t.name}</div>
              <div className="text-[10px] uppercase tracking-widest opacity-70">{t.pts}</div>
              <div className="text-xs mt-2">{t.perk}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Customers table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-[#F4A340]" /></div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <Award className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>Aucun membre encore. Les points sont crédités après chaque paiement confirmé.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left">
                  {['Client', 'Palier', 'Points', 'Balades', 'Commandes', 'Total dépensé'].map(h => (
                    <th key={h} className="px-4 py-3 font-semibold text-slate-600 uppercase text-xs tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((c) => {
                  const tier = TIER_META[c.tier] || TIER_META.Bronze;
                  return (
                    <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                      <td className="px-4 py-3">
                        <div className="font-medium">{c.full_name || c.email.split('@')[0]}</div>
                        <div className="text-xs text-slate-500">{c.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${tier.color}`}>
                          <span>{tier.icon}</span> {c.tier}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-bold text-[#E89534]">{c.points}</td>
                      <td className="px-4 py-3">{c.trips_count || 0}</td>
                      <td className="px-4 py-3">{c.orders_count || 0}</td>
                      <td className="px-4 py-3 font-semibold">{formatFCFA(c.total_spent || 0)} F</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoyaltyPanel;
